import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { ConfigService } from '@gitlab-org/config';
import { workspaceFolderPathFromUri } from '@gitlab-org/fs';
import { SecretRedactor } from '@gitlab-org/secret-redaction';
import { serializeSessionExport } from '@gitlab-lsp/workflow-api';
import type { ControllerApi } from '../../commands/tui/controller_api';
import {
  exportBudgetWarning,
  EXPORT_HOST_DETAIL_WARNING,
  EXPORT_UNANCHORED_PATHS_WARNING,
  hasUnanchoredHostPaths,
} from '../../commands/export/export_warnings';
import { SessionExportWriter } from '../../commands/export/session_export_writer';
import { SessionDataService, SessionManager } from '../../sessions';
import { SlashCommandAction, SlashCommandHandler } from '../slash_command_handler';

// `@Service` not `@Injectable`: the latter rejects concrete class tokens.
@Implements(SlashCommandHandler)
@Service({
  dependencies: [
    SessionManager,
    SessionDataService,
    SessionExportWriter,
    SecretRedactor,
    ConfigService,
    Logger,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultExportCommandHandler implements SlashCommandHandler {
  #logger: Logger;

  #sessionManager: SessionManager;

  #sessionDataService: SessionDataService;

  #writer: SessionExportWriter;

  #secretRedactor: SecretRedactor;

  #configService: ConfigService;

  constructor(
    sessionManager: SessionManager,
    sessionDataService: SessionDataService,
    writer: SessionExportWriter,
    secretRedactor: SecretRedactor,
    configService: ConfigService,
    logger: Logger,
  ) {
    this.#logger = withPrefix(logger, '[ExportCommandHandler]');
    this.#sessionManager = sessionManager;
    this.#sessionDataService = sessionDataService;
    this.#writer = writer;
    this.#secretRedactor = secretRedactor;
    this.#configService = configService;
  }

  command = {
    name: '/export',
    description: 'Export this session as a portable JSON bundle',
    action: SlashCommandAction.Export,
  } as const;

  async execute(api: ControllerApi): Promise<void> {
    this.#logger.info('Executing /export command');

    const session = this.#sessionManager.getActiveSession();
    if (!session?.sessionId) {
      api.showError('No active session to export.');
      return;
    }

    try {
      // The checkpoint, not session.elements, which are ChatElement not ChatLog.
      const source = await this.#sessionDataService.getSessionExportSource(session.sessionId);
      if (!source) {
        api.showError('This session could not be found on the GitLab instance.');
        return;
      }

      const workspaceRoot = this.#resolveWorkspaceRoot();
      const bundle = serializeSessionExport({
        sessionId: session.sessionId,
        chatLog: source.chatLog,
        workspaceRoot,
        redactor: this.#secretRedactor,
        title: source.title,
        flow: source.flow,
      });

      const path = await this.#writer.write(bundle, { cwd: workspaceRoot });

      const budgetWarning = exportBudgetWarning(bundle);
      const notes = [
        EXPORT_HOST_DETAIL_WARNING,
        ...(hasUnanchoredHostPaths(bundle) ? [EXPORT_UNANCHORED_PATHS_WARNING] : []),
        ...(budgetWarning ? [budgetWarning] : []),
      ];
      api.showInfo([`Exported this session to ${path}`, ...notes].join('\n'));
    } catch (error) {
      this.#logger.error('Failed to export session', error);
      api.showError(
        `Failed to export this session: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /** The root `CliInitializationService` already resolved for this session. */
  #resolveWorkspaceRoot(): string {
    const folders = this.#configService.get('workspaceFolders') || [];
    return folders.length > 0 ? workspaceFolderPathFromUri(folders[0].uri) : process.cwd();
  }
}
